import React, { useEffect, useMemo, useState } from "react";
import { CircleDollarSign, GitCompareArrows, PencilLine, Search, Sparkles } from "lucide-react";
import SearchInput from "../components/CommonComponents/SearchInput";
import EmptyState from "../components/CommonComponents/EmptyState";
import axiosInstance from "../services/axiosInstance";
import { showErrorToast } from "../utils/toast";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-PH", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-PH", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAction = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getActionMeta = (action) => {
  const normalized = String(action || "").toUpperCase();
  const actionMap = {
    CREATE: {
      label: "Create",
      Icon: CircleDollarSign,
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    },
    UPDATE: {
      label: "Update",
      Icon: PencilLine,
      className: "border-sky-200 bg-sky-50 text-sky-700",
    },
  };

  return actionMap[normalized] || {
    label: formatAction(action),
    Icon: Sparkles,
    className: "border-slate-200 bg-slate-50 text-slate-700",
  };
};

export default function ProductPriceLogsPage() {
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const loadLogs = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/admin/products/price-logs", {
        params: {
          page,
          limit: pageSize,
          ...(search ? { search } : {}),
        },
      });
      const nextLogs = response.data?.data || [];
      const reportedTotal = Number(response.data?.total || 0);
      const visibleTotal = (page - 1) * pageSize + nextLogs.length;
      setLogs(nextLogs);
      setTotalLogs(Math.max(reportedTotal, visibleTotal));
      setTotalPages(response.data?.totalPages || 1);
    } catch (error) {
      console.error("Failed to load product price logs:", error);
      showErrorToast("Failed to load product price logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadLogs(currentPage, searchTerm.trim());
    }, 250);

    return () => clearTimeout(timeout);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSuggestionSelect = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;

  const suggestions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return [];
    const uniqueNames = new Set();
    return logs
      .map((log) => log.product_name || log.product?.name || "")
      .filter((name) => {
        if (!name || uniqueNames.has(name)) return false;
        const matches = name.toLowerCase().includes(normalizedSearch);
        if (matches) uniqueNames.add(name);
        return matches;
      })
      .slice(0, 6);
  }, [logs, searchTerm]);

  return (
    <div className="flex flex-col gap-4 pt-3">
      <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              <GitCompareArrows className="h-4 w-4" />
              Product Pricing
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950">
              Product Price Logs
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Track universal and branch price changes for products.
            </p>
          </div>
          <div className="w-full lg:max-w-md">
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              onSuggestionSelect={handleSuggestionSelect}
              suggestions={suggestions}
              placeholder="Search logs by product name..."
              disabled={loading}
            />
          </div>
        </div>
      </section>

      {loading && logs.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          Loading price logs...
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No Price Logs"
          message={searchTerm ? "No logs match your search." : "Price changes will appear here after products are added or updated."}
        />
      ) : (
        <div className="flex flex-col gap-4" data-pagination-scroll-root>
          <div
            className="overflow-x-auto overflow-y-visible rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]"
            data-pagination-scroll-target
          >
            <table className="min-w-[1120px] w-full border-collapse text-sm text-slate-800">
              <thead
                className="sticky top-0 z-20 border-b"
                style={{
                  background: "linear-gradient(180deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 82%, black))",
                  borderColor: "color-mix(in srgb, var(--color-primary) 72%, black)",
                }}
              >
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Date</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Time</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Product</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Branch</th>
                  <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Old Price</th>
                  <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">New Price</th>
                  <th className="px-4 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Action</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-white/90 xl:px-5">Changed By</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const actionMeta = getActionMeta(log.action);
                  const ActionIcon = actionMeta.Icon;
                  return (
                    <tr
                      key={log.id}
                      className="border-b border-slate-100 bg-slate-50/85 transition hover:bg-emerald-50/70 even:bg-white last:border-0 sm:bg-transparent sm:hover:bg-slate-50/70 sm:even:bg-transparent"
                    >
                      <td className="px-4 py-3 text-sm text-slate-700 xl:px-5">{formatDate(log.createdAt || log.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 xl:px-5">{formatTime(log.createdAt || log.created_at)}</td>
                      <td className="px-4 py-3 xl:px-5">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{log.product_name || log.product?.name || "-"}</p>
                          <p className="mt-0.5 text-xs text-slate-500">{log.product?.product_code || "No barcode"}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 xl:px-5">{log.branch_name || log.branch?.name || "All branches"}</td>
                      <td className="px-4 py-3 text-center xl:px-5">
                        <span className="inline-flex min-w-[98px] items-center justify-center rounded-2xl border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                          {formatCurrency(log.old_price)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center xl:px-5">
                        <span
                          className="inline-flex min-w-[98px] items-center justify-center rounded-2xl border px-2 py-1 text-xs font-semibold shadow-sm"
                          style={{
                            background: "color-mix(in srgb, var(--color-primary) 9%, white)",
                            borderColor: "color-mix(in srgb, var(--color-primary) 18%, white)",
                            color: "var(--color-primary)",
                          }}
                        >
                          {formatCurrency(log.new_price)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center xl:px-5">
                        <span className={`inline-flex min-w-[106px] items-center justify-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${actionMeta.className}`}>
                          <ActionIcon className="h-3.5 w-3.5" />
                          {actionMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 xl:px-5">
                        {log.changed_by || log.changedBy?.username || log.changedBy?.email || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
            <div className="shrink-0 text-gray-500">
              Showing {totalLogs === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, totalLogs)} of {totalLogs} logs
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={safePage === 1}
              >
                Prev
              </button>
              <div className="text-xs text-gray-600">
                Page {safePage} of {totalPages}
              </div>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
